// packages/web-ui/src/AppShell/responsive-tokens.ts
//
// W2-S2 — responsive breakpoints + z-index stacking + panel widths + destination
// labels for the aria-live announcer.
//
// Sources of truth:
//   - UX §3.3 breakpoints table (lines 206–221)
//   - UX §3.5 z-index stack (lines 236–248)
//   - UX §4.6 + line 294 (destination announcer label)
//   - AC #1 / AC #6 / AC #7 of story 2-2

/**
 * Lower-bound px of each named tier. The `embed-narrow` tier is `< 600 px`.
 *
 * Boundaries are **inclusive** of the lower bound — width 1440 is `desktop-xl`,
 * width 1439 is `desktop`, etc.
 */
export const BREAKPOINTS = {
  desktopXl: 1440,
  desktop: 1280,
  narrowDesktop: 1024,
  tablet: 768,
  narrow: 600,
} as const;

export type Breakpoint =
  | 'desktop-xl'
  | 'desktop'
  | 'narrow-desktop'
  | 'tablet'
  | 'narrow'
  | 'embed-narrow';

/**
 * Returns the named breakpoint tier for a given pixel width.
 *
 * Pure function — no DOM access; safe in SSR / test environments.
 */
export function getBreakpoint(width: number): Breakpoint {
  if (width >= BREAKPOINTS.desktopXl) return 'desktop-xl';
  if (width >= BREAKPOINTS.desktop) return 'desktop';
  if (width >= BREAKPOINTS.narrowDesktop) return 'narrow-desktop';
  if (width >= BREAKPOINTS.tablet) return 'tablet';
  if (width >= BREAKPOINTS.narrow) return 'narrow';
  return 'embed-narrow';
}

/**
 * Z-index stack (UX §3.5). All AppShell-tree z-index values MUST reference
 * this constant — no magic numbers.
 */
export const Z_INDEX = {
  baseCanvas: 0,
  tabContentScroll: 1,
  resizeHandle: 5,
  healthStatusBanner: 10,
  statusBar: 10,
  tooltip: 50,
  popover: 50,
  modal: 100,
  toast: 200,
  dragOverlay: 999,
} as const;

/**
 * Default / clamp widths for the four AppShell regions. Sourced from AC #1 +
 * AC #6 of story 2-2.
 */
export const PANEL_DEFAULT_WIDTHS = {
  contextPanel: 260,
  contextPanelCompact: 240,
  utilityDrawer: 360,
  activityBar: 48,
  activityBarEmbed: 40,
  banner: 44,
  statusBar: 22,
  tabContentMin: 640,
} as const;

/**
 * Clamp ranges. ContextPanel additionally clamps at runtime to
 * `0.5 * window.innerWidth` (AC #1 + AC #2).
 */
export const PANEL_CLAMP = {
  contextPanelMin: 200,
  contextPanelMax: 1000,
  utilityDrawerMin: 320,
  utilityDrawerMax: 480,
} as const;

/**
 * Title-cased destination labels for the aria-live announcer (UX §4.6).
 * Keys mirror `HashRoute['kind']` from `packages/web-ui/src/router/hash-router.ts`.
 */
export const DESTINATION_LABELS = {
  chat: 'Chat',
  kb: 'KB Browser',
  files: 'Files',
  views: 'Views',
  settings: 'Settings',
  diff: 'Diff',
  git: 'Git',
  skills: 'Skills',
  unknown: 'Chat',
} as const;

export type DestinationKind = keyof typeof DESTINATION_LABELS;

/**
 * The 200 ms aria-live destination-announce delay (UX §4.6 + line 294).
 * Drops to 0 ms when `prefers-reduced-motion: reduce` is set.
 */
export const ARIA_LIVE_ANNOUNCE_DELAY_MS = 200;

/**
 * Resize-handle hot-zone width per UX §3.2 "Drag handles" row.
 */
export const RESIZE_HANDLE_WIDTH_PX = 4;
